/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { Injectable } from "@angular/core";
import { Inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { HttpContext } from "@angular/common/http";
import { HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { catchError } from "rxjs";
import { of } from "rxjs";
import { ENVIRONMENT_CONFIG } from "../../environments/environment-config";
import { EvironmentConfig } from "../../environments/environment-config";
import { map } from "rxjs/operators";
import { BYPASS_ORG_HEADER } from "@anvilor/ngx-apps-core";
import { API_BASE } from "@anvilor/ngx-apps-core";
import { bsonUtil } from "@anvilor/ngx-apps-core";
import { CollectionApiUtilService } from "@anvilor/ngx-apps-core";
import { strip } from "@anvilor/ngx-core-utils";
import { lstrip } from "@anvilor/ngx-core-utils";
import { AnvilorFormResponseDocument } from "../models/anvilor-form-response-document";
import { ResponseEditConfig } from "../models/submission-edit";
import { PostFormResponseEventData } from "../models/post-form-response-event-data";
import { AnvilorFormSubmissionUXConfig } from "../models/anvilor-form-config";

const RESPONSE_DATA_PATH = "/response/response_data";

export type PostCoffeeResponse = any;

export const ANVILOR_FORM_DOCUMENT_FILENAME = "anvilor-form.json";
export const CLIENT_DATA_DOCUMENT_FILENAME = "client-data.json";
export const EDIT_KEYS_FOLDER = "_edit_keys";

@Injectable({
  providedIn: "root",
})
export class AnvilorFormsApiService {
  constructor(
    @Inject(ENVIRONMENT_CONFIG) private env: EvironmentConfig,
    private http: HttpClient
  ) {}

  postCoffee(): Observable<PostCoffeeResponse> {
    return this.http.post<PostCoffeeResponse>(
      "/coffee",
      {},
      {
        context: new HttpContext()
          .set(BYPASS_ORG_HEADER, true)
          .set(API_BASE, this.env.apiBase),
      }
    );
  }

  postSubmission(
    data: PostFormResponseEventData
  ): Observable<AnvilorFormResponseDocument> {
    const attachments = data?.attachments;
    const payloadData: Omit<PostFormResponseEventData, "attachments"> = {
      response_data: data.response_data,
      anvilor_form_oid: data.anvilor_form_oid,
      edit_key: data.edit_key,
      response_oid: data.response_oid,
      deployment_key: data.deployment_key,
      deployment_token: data.deployment_token,
      grecaptcha_v2_response: data.grecaptcha_v2_response,
      payload: data.payload,
    };
    const payload = CollectionApiUtilService.makePayload(
      bsonUtil.serialize(payloadData),
      attachments
    );
    let headers = new HttpHeaders();
    if (data.auth_header) {
      headers = headers.set("Authorization", data.auth_header);
    }
    return this.http
      .post<AnvilorFormResponseDocument>("/anvilor_forms_submission", payload, {
        context: new HttpContext()
          .set(BYPASS_ORG_HEADER, true)
          .set(API_BASE, this.env.apiBase),
        headers,
      })
      .pipe(map(({ doc }) => doc as AnvilorFormResponseDocument));
  }

  /**
   * Helper to load a file from Survey configs CDN relative to the path in
   * the browser path.
   */
  private getConfigsCdnFile<T extends any = any>(relpath: string): Observable<T> {
    const basePath = "/" + strip(window.location.pathname, "/") + "/";
    return this.http.get<T>(basePath + lstrip(relpath, "/"), {
      context: new HttpContext().set(API_BASE, this.env.configApiBase),
    });
  }

  getAnvilorFormConfigAtCurrentPath(): Observable<AnvilorFormSubmissionUXConfig> {
    return this.getConfigsCdnFile<AnvilorFormSubmissionUXConfig>(
      ANVILOR_FORM_DOCUMENT_FILENAME
    );
  }

  getClientDataAtCurrentPath(): Observable<any | null> {
    return this.getConfigsCdnFile<any>(CLIENT_DATA_DOCUMENT_FILENAME).pipe(
      catchError(() => of(null))
    );
  }

  getEditConfig(editKey: string): Observable<ResponseEditConfig> {
    const relpath = `${EDIT_KEYS_FOLDER}/${editKey}.json`;
    return this.getConfigsCdnFile<ResponseEditConfig>(relpath);
  }
}
